import { DeploymentSkipReason } from '@pubstack/common/src/deployment-center'
import { FunctionComponent } from 'react'
import Button from '~/components/Button'
import { label, prefix } from '~/components/layout/ProductTrackingIntegration'
import { Modal } from '~/components/Modal'

/** Why a row the server resolved will not be written, in words a publisher can act on. */
const SKIP_REASON_LABEL: Record<DeploymentSkipReason, string> = {
  outdated: 'changed since this page was loaded',
  'not-found': 'no longer exists',
  'wrong-scope': 'belongs to another scope',
}

export type ConfirmationItem = { stackGroupId: string; mediaName: string; stackName: string; latestVersion: number }

type Props = {
  actionLabel: string
  description: string
  /** What the server resolved — the number the action will actually cover. */
  total: number
  skipped: { stackGroupId: string; reason: DeploymentSkipReason }[]
  /**
   * The rows themselves, listed only when the user picked them by hand and there are few enough to read.
   * Absent for a whole-queue run, which is the case this modal exists to keep summary-shaped.
   */
  items?: ConfirmationItem[]
  /**
   * How many of the groups the run covers are mid-experiment, counted server-side by the plan. Absent on
   * the actions that move no traffic — only a deploy can change what a running experiment is.
   */
  abDeployed?: number
  /**
   * The modal's own base label, without an action suffix — it appends `-cancel` and `-confirm` itself,
   * which is why this is not a finished `TrackingLabel`.
   */
  trackingLabel: `${prefix}-${label}`
  onCancel: () => void
  onConfirm: () => void
}

/** Above this many rows the list stops being information and becomes a wall. */
const MAX_LISTED_ITEMS = 25

/**
 * The confirmation for a bulk action, sized for a queue rather than for a selection.
 *
 * The old modal rendered one table row per stack, which was fine when the page could only ever act on
 * what one screen held. A thousand-stack deploy is the case this page was rebuilt for, and printing a
 * thousand rows in a modal tells the user nothing they can check — so the count and the exceptions are
 * the content, and the per-row list survives only for a hand-picked selection small enough to read.
 *
 * Skipped rows are shown rather than quietly subtracted: a large queue routinely holds a few groups the
 * action must refuse, and "982 deployed, 18 skipped" is the honest version of that.
 */
export const ConfirmDeploymentModal: FunctionComponent<Props> = ({ actionLabel, description, total, skipped, items, abDeployed, trackingLabel, onCancel, onConfirm }) => {
  const skippedByReason = skipped.reduce<Partial<Record<DeploymentSkipReason, number>>>((counts, { reason }) => ({ ...counts, [reason]: (counts[reason] ?? 0) + 1 }), {})
  const listed = items && items.length <= MAX_LISTED_ITEMS ? items : undefined

  return (
    <Modal.Content>
      <Modal.Title>{actionLabel}</Modal.Title>
      <Modal.Body>
        <div className={'flex flex-col gap-4'}>
          <div>{description}</div>
          <div className={'text-lg font-bold'}>
            {total} stack{total > 1 ? 's' : ''}
          </div>

          {listed && (
            <ul className={'flex max-h-72 flex-col gap-1 overflow-y-auto'}>
              {listed.map((item) => (
                <li className={'flex justify-between gap-4 text-hurricane'} key={item.stackGroupId}>
                  <span>
                    {item.mediaName} — {item.stackName}
                  </span>
                  <span>v{item.latestVersion}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Deploying an A/B group is allowed, and it is the one action that changes the experiment — so
              what it does to the split is said here, where it is still cancellable, rather than left to be
              discovered on the media afterwards. */}
          {abDeployed !== undefined && abDeployed !== 0 && (
            <div className={'flex flex-col gap-1 rounded bg-warning-soft p-3 text-warning-ink'}>
              <span className={'font-bold'}>
                {abDeployed} stack{abDeployed > 1 ? 's are' : ' is'} A/B deployed
              </span>
              <span>
                Deploying one changes its experiment: the new version replaces the newest live version and takes over that version’s share of the traffic. The other live versions keep serving their
                own.
              </span>
            </div>
          )}

          {skipped.length > 0 && (
            <div className={'flex flex-col gap-1 rounded bg-warning-soft p-3 text-warning-ink'}>
              <span className={'font-bold'}>
                {skipped.length} stack{skipped.length > 1 ? 's' : ''} will be skipped
              </span>
              {Object.entries(skippedByReason).map(([reason, count]) => (
                <span key={reason}>
                  {count} {SKIP_REASON_LABEL[reason as DeploymentSkipReason]}
                </span>
              ))}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Actions>
        <Button variant={'tertiary'} onClick={onCancel} trackingLabel={`${trackingLabel}-cancel`}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={total === 0} trackingLabel={`${trackingLabel}-confirm`}>
          Confirm
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
